import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, getDoc, setDoc } from "firebase/firestore";
import Loader from "../components/Loader";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

function SalesAnalysis() {
  // 🔥 Context se Roles aur Current User le rahe hain
  const { orders, users, loading: contextLoading, isAdmin, currentUser } = useWebData();
  
  const [loading, setLoading] = useState(true);
  const [selectedEmp, setSelectedEmp] = useState("All");
  const [empTargets, setEmpTargets] = useState({}); 

  const [chartData, setChartData] = useState(null);
  const [currentSales, setCurrentSales] = useState(0);
  const [currentTarget, setCurrentTarget] = useState(0);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [newTarget, setNewTarget] = useState("");

  // 🔥 1. Initial State Set karna (Admin vs Employee)
  useEffect(() => {
      if(currentUser && !contextLoading) {
          if(isAdmin) {
              // Admin hai to default "All" rahega (Jo already useState me hai)
          } else {
              // Employee hai to FORCE selection to his name
              setSelectedEmp(currentUser.name);
          }
      }
  }, [isAdmin, currentUser, contextLoading]);

  // 2. Fetch Targets
  useEffect(() => {
    setLoading(true);
    const fetchTargets = async () => {
        try {
            const docRef = doc(db, "settings", "targets");
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setEmpTargets(docSnap.data());
            }
        } catch(e) { console.log("Error fetching targets", e); }
        setLoading(false);
    };
    fetchTargets();
  }, []); 

  // 🔥 3. Process Data Logic
  useEffect(() => {
      const safeOrders = orders || [];

      // Filter Logic
      const filteredOrders = selectedEmp === "All" 
          ? safeOrders 
          : safeOrders.filter(o => (o.senderName === selectedEmp || o.userName === selectedEmp));

      // Target Logic
      let targetVal = 0;
      if (selectedEmp === "All") {
          targetVal = Object.values(empTargets).reduce((a, b) => Number(a) + Number(b), 0) || 10000000;
      } else {
          targetVal = Number(empTargets[selectedEmp]) || 500000; 
      }
      setCurrentTarget(targetVal);

      // Monthly Sales Logic
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const monthlySales = new Array(12).fill(0);
      let totalSales = 0;

      filteredOrders.forEach(order => {
          if (order.status !== 'Rejected') {
             const date = new Date(order.date || order.createdAt);
             const amount = parseFloat(order.amount) || 0;
             
             if (!isNaN(date.getTime())) {
                 monthlySales[date.getMonth()] += amount;
                 totalSales += amount;
             }
          }
      });

      setCurrentSales(totalSales);

      // Chart Data
      setChartData({
          bar: {
              labels: months,
              datasets: [{
                  label: `Sales Trend (${selectedEmp})`,
                  data: monthlySales,
                  backgroundColor: selectedEmp === "All" ? '#3498db' : '#8e44ad',
                  borderRadius: 5,
              }]
          },
          doughnut: {
              labels: ['Achieved', 'Remaining'],
              datasets: [{
                  data: [totalSales, Math.max(0, targetVal - totalSales)],
                  backgroundColor: ['#27ae60', '#ecf0f1'],
                  borderWidth: 0,
              }]
          }
      });

  }, [orders, selectedEmp, empTargets]);

  // Target Save karna
  const saveTarget = async () => {
      if(!newTarget) return;
      if(selectedEmp === "All") return alert("Please select an employee to set target.");

      const updatedTargets = { ...empTargets, [selectedEmp]: Number(newTarget) };
      
      await setDoc(doc(db, "settings", "targets"), updatedTargets, { merge: true });
      setEmpTargets(updatedTargets);
      setShowTargetModal(false);
      alert(`Target for ${selectedEmp} updated to ₹${newTarget}`);
  };

  if(loading || contextLoading) return <Loader />;

  return (
    <div style={{padding:"20px", fontFamily:"Arial", backgroundColor:"#f4f6f8", minHeight:"100vh"}}>
      
      {/* HEADER */}
      <div style={headerStyle}>
          <div>
              <h2 style={{color:"#2c3e50", margin:0}}>📊 Sales Analysis</h2>
              <p style={{margin:0, fontSize:"12px", color:"gray"}}>Performance & Targets</p>
          </div>

          <div style={{display:"flex", gap:"10px", alignItems:"center"}}>
              
              {/* 🔥🔥 DROPDOWN LOGIC FIX 🔥🔥 */}
              {isAdmin ? (
                  // Admin: Dropdown Dikhayein
                  <select 
                      value={selectedEmp} 
                      onChange={(e)=>setSelectedEmp(e.target.value)} 
                      style={selectStyle}
                  >
                      <option value="All">🏢 All Company</option>
                      {(users || []).map((u, i) => (
                          <option key={u.id || i} value={u.name}>👤 {u.name}</option>
                      ))}
                  </select>
              ) : (
                  // Employee: Fixed Name Dikhayein
                  <div style={{...selectStyle, backgroundColor:"#eef2f3", color:"#555", cursor:"not-allowed"}}>
                      👤 {currentUser?.name || selectedEmp}
                  </div>
              )}

              {/* Set Target Button - Sirf Admin ke liye */}
              {isAdmin && selectedEmp !== "All" && (
                  <button onClick={() => setShowTargetModal(true)} style={btnTarget}>🎯 Set Target</button>
              )}
          </div>
      </div>

      {/* STATS CARDS */}
      <div style={{display:"flex", gap:"20px", marginBottom:"20px", flexWrap:"wrap"}}>
          <div style={statCard}>
              <span style={{color:"gray", fontSize:"12px"}}>Total Sales Achieved</span>
              <div style={{fontSize:"24px", fontWeight:"bold", color:"#27ae60"}}>₹{currentSales.toLocaleString()}</div>
          </div>
          <div style={statCard}>
              <span style={{color:"gray", fontSize:"12px"}}>Assigned Target (Yearly)</span>
              <div style={{fontSize:"24px", fontWeight:"bold", color:"#2c3e50"}}>₹{currentTarget.toLocaleString()}</div>
          </div>
          <div style={statCard}>
              <span style={{color:"gray", fontSize:"12px"}}>Performance</span>
              <div style={{fontSize:"24px", fontWeight:"bold", color: currentSales >= currentTarget ? "green" : "orange"}}>
                  {((currentSales / (currentTarget || 1)) * 100).toFixed(1)}%
              </div>
          </div>
      </div>

      <div style={gridStyle}>
          {/* CHART 1 */}
          <div style={cardStyle}>
              <h3 style={cardTitle}>📅 Monthly Trend</h3>
              <div style={{height:"300px"}}>
                  {chartData && <Bar data={chartData.bar} options={{ responsive: true, maintainAspectRatio: false }} />}
              </div>
          </div>

          {/* CHART 2 */}
          <div style={cardStyle}>
              <h3 style={cardTitle}>🎯 Target Status</h3>
              <div style={{height:"250px", display:"flex", justifyContent:"center", position:"relative"}}>
                  {chartData && <Doughnut data={chartData.doughnut} options={{ responsive: true, maintainAspectRatio: false }} />}
                  <div style={{position:"absolute", top:"45%", textAlign:"center"}}>
                      <div style={{fontSize:"20px", fontWeight:"bold"}}>{((currentSales/(currentTarget||1))*100).toFixed(0)}%</div>
                      <div style={{fontSize:"10px", color:"gray"}}>Achieved</div>
                  </div>
              </div>
          </div>
      </div>

      {/* TARGET MODAL */}
      {showTargetModal && (
          <div style={modalOverlay}>
              <div style={modalContent}>
                  <h3>Set Target for {selectedEmp}</h3>
                  <input 
                      type="number" 
                      placeholder="Enter Yearly Amount (₹)" 
                      value={newTarget} 
                      onChange={(e)=>setNewTarget(e.target.value)} 
                      style={modalInput} 
                  />
                  <div style={{marginTop:"15px", textAlign:"right"}}>
                      <button onClick={()=>setShowTargetModal(false)} style={btnCancel}>Cancel</button>
                      <button onClick={saveTarget} style={btnSave}>Save Target</button>
                  </div>
              </div>
          </div>
      )}

    </div>
  );
}

// STYLES
const headerStyle={display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"20px", background:"white", padding:"15px", borderRadius:"10px", boxShadow:"0 2px 5px rgba(0,0,0,0.05)", flexWrap:"wrap"};
const selectStyle={padding:"10px", borderRadius:"5px", border:"1px solid #ccc", fontSize:"14px", fontWeight:"bold", minWidth:"200px"};
const btnTarget={padding:"10px 15px", backgroundColor:"#e67e22", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontWeight:"bold"};
const statCard={flex:1, background:"white", padding:"15px", borderRadius:"10px", boxShadow:"0 2px 5px rgba(0,0,0,0.05)", borderLeft:"4px solid #3498db", minWidth:"200px"};
const gridStyle={display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(400px, 1fr))", gap:"20px"};
const cardStyle={backgroundColor:"white", padding:"20px", borderRadius:"12px", boxShadow:"0 4px 10px rgba(0,0,0,0.05)", border:"1px solid #eee"};
const cardTitle={margin:"0 0 20px 0", color:"#34495e", fontSize:"16px", borderBottom:"1px solid #f0f0f0", paddingBottom:"10px"};
const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"20px", borderRadius:"10px", width:"350px", boxShadow:"0 5px 15px rgba(0,0,0,0.3)"};
const modalInput={width:"100%", padding:"10px", margin:"10px 0", borderRadius:"5px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"16px"};
const btnSave={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer"};

export default SalesAnalysis;